// src/pages/agent/Training.js
// Role-aware training guide.
//   admin / agent  → full pricing/closing walkthrough (comps → MAO → offer → follow-up)
//   lead_submitter → "find leads on Zillow + submit them" simplified flow
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Card, Btn, BURG, BURG_DK, GOLD, GOLD_PALE, CREAM, MID, LIGHT, BORDER } from '../../components/agent/UI';
import { useAgentAuth } from '../../lib/auth';

const CHAPTERS = [
  { id: 'overview',  title: 'Overview',                  num: '⓪' },
  { id: 'comps',     title: 'Find Comparable Sales',     num: '①' },
  { id: 'arv',       title: 'Set the After-Repair Value (ARV)', num: '②' },
  { id: 'repairs',   title: 'Estimate the Rehab',        num: '③' },
  { id: 'numbers',   title: 'Run the Numbers (MAO)',     num: '④' },
  { id: 'offer',     title: 'Make the Offer',            num: '⑤' },
  { id: 'followup',  title: 'Follow Up Until They Sign', num: '⑥' },
  { id: 'cheatsheet',title: 'Quick reference',           num: '★' }
];

export default function Training() {
  const { profile } = useAgentAuth();
  if (profile?.role === 'lead_submitter') return <LeadSubmitterGuide />;
  return <AgentGuide />;
}

function AgentGuide() {
  const navigate = useNavigate();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <PageHeader
        eyebrow="Training guide"
        title={<>How to <em style={{ fontStyle: 'italic', color: BURG }}>price</em>, <em style={{ fontStyle: 'italic', color: BURG }}>pitch</em>, and close a wholesale deal in HomeIQa.</>}
        sub="Six steps from a fresh lead to a signed assignment contract. Each chapter pairs the wholesaling concept with the exact HomeIQa workflow — what to click, what to type, what to expect."
      />

      {/* Source video reference */}
      <Card
        title="Watch the source walkthrough"
        right={<a href="https://www.youtube.com/watch?v=PWsD30nU7Ak" target="_blank" rel="noopener noreferrer" style={s.sourceLink}>Open on YouTube ↗</a>}
      >
        <div style={s.videoNote}>
          The chapters below are this video, mapped to <strong>exactly which buttons to click in HomeIQa</strong> for every step. Watch the source first — the host walks through Zillow comping, ARV ceiling logic, rehab tiers, and the 70/75/80% rule with real numbers. Then come back and run the same flow against a real lead in your dashboard.
        </div>
        <div style={s.videoFrame}>
          <iframe
            src="https://www.youtube.com/embed/PWsD30nU7Ak"
            title="How to find ARV, comps, repair costs, and offer prices for wholesale real estate"
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={s.videoIframe}
          />
        </div>
      </Card>

      {/* Table of contents */}
      <Card title="Chapters">
        <ol style={s.toc}>
          {CHAPTERS.map(c => (
            <li key={c.id} style={s.tocItem}>
              <button onClick={() => scrollTo(c.id)} style={s.tocLink}>
                <span style={s.tocNum}>{c.num}</span>
                <span>{c.title}</span>
              </button>
            </li>
          ))}
        </ol>
      </Card>

      {/* ── 0. Overview ── */}
      <Section id="overview" num="⓪" title="Overview" subtitle="The whole flow in 90 seconds">
        <p>
          Wholesaling is simple in theory: <strong>find a motivated seller, lock the property under contract well below market value, and sell that contract to a cash buyer for a fee.</strong>
          The hard part is pricing the deal correctly so a cash buyer says yes — and so the seller signs in the first place.
        </p>
        <p>
          This guide walks you through the exact pricing workflow inside HomeIQa. The shortcut formula:
        </p>
        <Formula
          label="The Wholesale Formula"
          body={<>(ARV × 70%) − Repairs − Your Assignment Fee = <strong>MAO</strong></>}
          note="MAO = Maximum Allowable Offer. Offer slightly below MAO so the seller has negotiation room."
        />
        <p>
          Every step that follows is about getting the four numbers in that formula right: <strong>ARV</strong>, the <strong>70%</strong> (or 75–80% in hot markets), <strong>Repairs</strong>, and your <strong>Assignment Fee</strong>.
        </p>
      </Section>

      {/* ── 1. Comps ── */}
      <Section id="comps" num="①" title="Find Comparable Sales" subtitle="Step 1 — Pull at least 3 sold comps within 1 mile, ≤ 6 months old">
        <Concept>
          <strong>Comps</strong> are recently sold houses similar to your subject property. They tell you what an investor (or retail buyer) will actually pay. Look for <em>sold</em>, not <em>listed</em> — listed prices are wishes, sold prices are reality.
        </Concept>

        <H3>How to do it in HomeIQa</H3>
        <ol style={s.list}>
          <li>Open any lead → scroll to the <strong>Comparable sales</strong> panel just below the property details.</li>
          <li>If the panel is empty or the data is older than 7 days, click <strong>🔄 Refresh</strong>. RentCast pulls 6 closest comps within 1.5 miles, sold within the last 9 months.</li>
          <li>Inspect each comp's row: address, beds/baths/sqft, year built, sold date, price, distance, and a <strong>match-percentage pill</strong>.</li>
          <li><strong>Match % colors:</strong> green ≥ 85% (very similar), gold 70–85% (close), gray below (worth checking but lower weight).</li>
        </ol>

        <H3>What good comps look like</H3>
        <Bullets>
          <li><strong>Similar beds/baths/sqft</strong> — within 1 bed/bath, ±20% sqft</li>
          <li><strong>Within 1 mile</strong> — closer is better; school district matters most</li>
          <li><strong>Sold in the last 6 months</strong> — older comps misrepresent today's market</li>
          <li><strong>Similar condition</strong> — a remodeled comp ≠ a tired-condition comp</li>
        </Bullets>

        <Tip>
          <strong>Pro move:</strong> When you skim the comps table, look for one comp that is <em>"move-in ready and just sold"</em> — that's your <strong>ARV ceiling</strong>. Then look for one comp that's <em>"needs updates and just sold"</em> — that gives you a sense of what your seller's tired property might fetch as-is.
        </Tip>

        <H3>Where the data comes from</H3>
        <p>
          HomeIQa calls the <strong>RentCast AVM API</strong> on every lead. Each lookup costs 1 credit — your free RentCast plan gives you 50/month. We also cache: same lead won't re-pull for 7 days unless you click Refresh.
        </p>
      </Section>

      {/* ── 2. ARV ── */}
      <Section id="arv" num="②" title="Set the After-Repair Value (ARV)" subtitle="Step 2 — What the property is worth fully fixed up">
        <Concept>
          <strong>ARV (After Repair Value)</strong> is what the property would sell for if it were fully renovated and listed retail today. <em>Not what it's worth right now.</em> ARV is the ceiling — the cash buyer's exit price.
        </Concept>

        <H3>How to do it in HomeIQa</H3>
        <ol style={s.list}>
          <li>Look at the <strong>"Estimated value (AVM)"</strong> headline at the top of the Comps panel — that's RentCast's automated estimate. Treat it as a starting point, not gospel.</li>
          <li>Compare the AVM to the <strong>median comp</strong> shown to the right.</li>
          <li>Cross-check with Zillow's Zestimate and the <em>"move-in ready"</em> comps you identified in Step 1.</li>
          <li>When you're confident, click <strong>"Use this"</strong> next to the ARV field in the Calculator to seed your ARV.</li>
        </ol>

        <Example label="Worked example">
          <strong>Subject:</strong> 537 Helen St, 2 bd / 1 ba / 864 sqft<br/>
          <strong>RentCast AVM:</strong> $148,000<br/>
          <strong>Median comp:</strong> $141,500 (3 comps within 0.4 mi)<br/>
          <strong>Best move-in ready comp:</strong> $172,000 (after ~$50K of updates)<br/>
          <strong>→ ARV chosen:</strong> <strong>$155,000</strong> (mid-point of AVM and the renovated comp, since this property would update nicely but isn't in the absolute prime sub-block)
        </Example>

        <Tip>
          <strong>When in doubt, go conservative.</strong> Setting ARV $10K low protects your cash buyer's margin and keeps your offer tight. Setting it $20K high makes the deal undeliverable.
        </Tip>
      </Section>

      {/* ── 3. Repairs ── */}
      <Section id="repairs" num="③" title="Estimate the Rehab" subtitle="Step 3 — How much it costs the cash buyer to fix the place up">
        <Concept>
          The repair number is <em>the cash buyer's cost</em>, not yours. You're not doing the work — you're forecasting what they'll spend. Get this wrong and your buyer walks at the table.
        </Concept>

        <H3>HomeIQa's repair tiers</H3>
        <p>The Calculator's repair tier dropdown applies a $/sqft multiplier:</p>
        <table style={s.table}>
          <thead>
            <tr><th style={s.th}>Tier</th><th style={s.th}>$/sqft</th><th style={s.th}>What it covers</th></tr>
          </thead>
          <tbody>
            <tr><td style={s.td}><strong>Super Light</strong></td><td style={s.tdNum}>$5</td><td style={s.td}>Paint touch-up, deep clean, minor fix-ups. Basically rent-ready.</td></tr>
            <tr><td style={s.td}><strong>Light</strong></td><td style={s.tdNum}>$15</td><td style={s.td}>Paint everything, new flooring in 1–2 rooms, basic landscaping.</td></tr>
            <tr><td style={s.td}><strong>Moderate</strong></td><td style={s.tdNum}>$25</td><td style={s.td}>New flooring throughout, painted cabinets, new appliances, full paint.</td></tr>
            <tr><td style={s.td}><strong>Heavy</strong></td><td style={s.tdNum}>$40</td><td style={s.td}>Cosmetics + roof OR HVAC OR major plumbing/electrical.</td></tr>
            <tr><td style={s.td}><strong>Full Gut</strong></td><td style={s.tdNum}>$70</td><td style={s.td}>Everything — down to studs in places. Roof, HVAC, electrical, plumbing, kitchen, baths.</td></tr>
          </tbody>
        </table>

        <H3>Real-world calibration (from the field)</H3>
        <Bullets>
          <li><strong>Cosmetics-only flip on a 1,500–2,200 sqft house: $40–$50K total.</strong> No new roof, no HVAC, no foundation. Just paint, flooring, kitchen refresh, appliances.</li>
          <li>Add <strong>~$30–40K</strong> if the property also needs roof, HVAC, electrical, or plumbing.</li>
          <li>Material prices swing — lumber alone can change weekly. The tiers are a starting point; refine with one or two contractors over time.</li>
        </Bullets>

        <H3>How to do it in HomeIQa</H3>
        <ol style={s.list}>
          <li>In the Calculator, set the <strong>Square feet</strong> from the property record.</li>
          <li>Pick a <strong>Repair tier</strong> based on the listing photos and any condition notes from your call.</li>
          <li>Override the <strong>$/sqft</strong> manually if you have a contractor quote.</li>
          <li>Watch the <strong>Repair Cost</strong> result update in real time.</li>
        </ol>

        <Tip>
          <strong>Tier-picking shortcut:</strong> Look at the listing photos. <em>"Carpet in the bathroom, dated cabinets, kitchen from 1985"</em> = Moderate. Add Heavy if you spot stained ceilings (water damage), exposed wires, or if the listing says "as-is needs work."
        </Tip>
      </Section>

      {/* ── 4. Numbers ── */}
      <Section id="numbers" num="④" title="Run the Numbers (MAO)" subtitle="Step 4 — Walk away with a max offer you can defend">
        <Concept>
          The <strong>MAO</strong> (Maximum Allowable Offer) is the highest price you can pay the seller and still leave the cash buyer enough margin to flip profitably plus your fee. <em>Pay more than MAO and you can't move the contract.</em>
        </Concept>

        <Formula
          label="The Formula"
          body={<>(ARV × Location %) − Repairs − Your Assignment Fee = <strong>MAO</strong></>}
          note="Default: 70% (the classic '70% rule'). In hot markets with lots of competition or expensive properties, 75–80% is common."
        />

        <H3>How to do it in HomeIQa</H3>
        <ol style={s.list}>
          <li>Calculator already auto-fills <strong>ARV</strong> from RentCast (or your override).</li>
          <li>Set <strong>Location %</strong> — keep at 70 for normal markets, 75–80 for hot markets, 60–65 for risky/declining areas.</li>
          <li>Set <strong>Assignment fee</strong> — typical $10–$15K for sub-$200K properties, $15–$25K for $200K–$500K, more for higher-priced.</li>
          <li>Read <strong>Cash MAO</strong> in the result panel — that's your ceiling.</li>
        </ol>

        <Example label="Worked example — 537 Helen St">
          <strong>ARV:</strong> $155,000<br/>
          <strong>Sqft:</strong> 864<br/>
          <strong>Tier:</strong> Moderate ($25/sqft) → <strong>Repairs = $21,600</strong><br/>
          <strong>Location:</strong> 70%<br/>
          <strong>Assignment Fee:</strong> $10,000<br/>
          <strong>Calculation:</strong> ($155,000 × 70%) − $21,600 − $10,000 = $108,500 − $31,600 = <strong>$76,900 MAO</strong><br/>
          <em>This is the max you can pay the seller.</em> The seller is asking $94,900 — you're $18K apart. Either negotiate down or pass.
        </Example>

        <H3>The 75% / 80% sliding scale</H3>
        <p>The 70% rule is a convention, not a law. When pulling Location % off 70:</p>
        <Bullets>
          <li><strong>Higher (75–80%)</strong> — competitive market, $500K+ properties (cash buyers will accept thinner margin on bigger absolute dollars), strong appreciation, low days-on-market on Zillow.</li>
          <li><strong>Lower (60–65%)</strong> — declining market, war-zone neighborhoods, properties with title issues, high days-on-market.</li>
          <li><strong>Million-dollar properties</strong> can sometimes work at 90% — a flipper still nets $100K on the deal.</li>
        </Bullets>

        <Tip>
          <strong>Don't auto-buy the 70% rule.</strong> Ask: "Would a cash buyer I know actually pay this number?" If the answer is no, your MAO is wrong — adjust ARV, repairs, or your fee.
        </Tip>
      </Section>

      {/* ── 5. Offer ── */}
      <Section id="offer" num="⑤" title="Make the Offer" subtitle="Step 5 — Lock the property under contract">
        <Concept>
          Your first offer is <em>below</em> MAO so you have room to negotiate up. Never lead with your ceiling. Aim 10–15% under MAO on the first ask.
        </Concept>

        <H3>How to do it in HomeIQa</H3>
        <ol style={s.list}>
          <li>From the lead, hit the pulsing gold <strong>Intro</strong> SMS pill in the SMS thread to break the ice if you haven't yet.</li>
          <li>Once they're warm, go to the <strong>Contracts panel</strong> on the lead.</li>
          <li>Pick contract type → <strong>Purchase & Sale Agreement</strong>.</li>
          <li>Fill in: purchase price (your offer, NOT your MAO), acceptance deadline (typically 7 days), closing date (typically 30), and the seller's email.</li>
          <li>Click <strong>Preview</strong> to make sure everything looks right.</li>
          <li>Click <strong>Generate & email for signature</strong>. The seller gets a tokenized signing link via Mailgun. The lead's status moves to <strong>Negotiating</strong>.</li>
        </ol>

        <Example label="Worked example — opening offer">
          <strong>Your MAO:</strong> $76,900<br/>
          <strong>Offer 10–15% under:</strong> $65,000–$69,000<br/>
          <strong>You send:</strong> $67,500. <em>Why?</em> Round-ish number, plenty of room to come up to mid-70s if they push back, far enough below their $94,900 ask that it telegraphs you're serious about a quick close.
        </Example>

        <Tip>
          <strong>Watch the contracts panel update live.</strong> <em>Sent → Viewed → Signed</em> all flip in real time. If the seller doesn't open within 24 hours, send a follow-up SMS.
        </Tip>
      </Section>

      {/* ── 6. Follow Up ── */}
      <Section id="followup" num="⑥" title="Follow Up Until They Sign" subtitle="Step 6 — The deal is in the follow-up">
        <Concept>
          <strong>Most deals close on touch #3, #4, or #5 — not the first touch.</strong> The discipline is the cadence: 24h, 3 days, 1 week, 1 month, 3 months. Some sellers say no in March and yes in June.
        </Concept>

        <H3>How to do it in HomeIQa</H3>
        <ol style={s.list}>
          <li>From the lead's SMS thread, click any of the gold <strong>Quick Send</strong> pills. The textarea pre-fills with the message — interpolated for that specific lead's first name, city, state, street.</li>
          <li>Stay on the cadence: <strong>24h after no response → 3 days → 1 week → 1 month → 3 months.</strong></li>
          <li>Log every call as a <strong>Note</strong> with the right type (Initial Call, Follow Up, Renegotiation, Voicemail, Objection).</li>
          <li>Watch the <strong>Awaiting response</strong> tile on the dashboard. If a lead is sitting there with no reply, it's overdue for the next pill.</li>
        </ol>

        <H3>Daily Rule for Success</H3>
        <Formula
          label="Make 2–3 offers per day. Not per week."
          body={<><strong>More offers = more deals.</strong></>}
          note="Volume is the engine. A wholesaler making 60 offers a month outperforms one making 10, every time."
        />

        <Tip>
          <strong>The follow-up math:</strong> If you make 3 offers/day × 22 working days = 66 offers/month. At a 5% close rate that's 3.3 deals. At $10K average assignment fee, that's $33K/month. The math only works with consistency.
        </Tip>
      </Section>

      {/* ── Cheat sheet ── */}
      <Section id="cheatsheet" num="★" title="Quick reference" subtitle="One-pager you can print and tape to your monitor">
        <Card title="The whole flow on one screen">
          <table style={s.cheat}>
            <tbody>
              <tr><td style={s.cheatStep}>①</td><td style={s.cheatLabel}>Pull comps</td><td style={s.cheatHow}>Open lead → Comps panel → 🔄 Refresh</td></tr>
              <tr><td style={s.cheatStep}>②</td><td style={s.cheatLabel}>Set ARV</td><td style={s.cheatHow}>Click "Use this" beside AVM, or override</td></tr>
              <tr><td style={s.cheatStep}>③</td><td style={s.cheatLabel}>Estimate repairs</td><td style={s.cheatHow}>Calculator → pick tier → confirm $/sqft</td></tr>
              <tr><td style={s.cheatStep}>④</td><td style={s.cheatLabel}>Calc MAO</td><td style={s.cheatHow}>Set Location %, Assignment fee → read Cash MAO</td></tr>
              <tr><td style={s.cheatStep}>⑤</td><td style={s.cheatLabel}>Send offer</td><td style={s.cheatHow}>Contracts panel → PSA → 10–15% below MAO</td></tr>
              <tr><td style={s.cheatStep}>⑥</td><td style={s.cheatLabel}>Follow up</td><td style={s.cheatHow}>SMS Quick Send pills · 24h, 3d, 1w, 1mo, 3mo</td></tr>
            </tbody>
          </table>
        </Card>

        <Card title="Numbers to memorize">
          <Bullets>
            <li><strong>Location % default:</strong> 70 (raise to 75–80 in hot markets)</li>
            <li><strong>Cosmetic flip:</strong> $40–50K on 1,500–2,200 sqft</li>
            <li><strong>Cosmetic + roof/HVAC:</strong> add $30–40K</li>
            <li><strong>Typical assignment fee:</strong> $10–15K (sub-$200K), $15–25K ($200K–$500K)</li>
            <li><strong>First offer:</strong> 10–15% below MAO</li>
            <li><strong>Daily target:</strong> 2–3 offers per day</li>
          </Bullets>
        </Card>

        <Card title="Where each thing lives in HomeIQa">
          <Bullets>
            <li><strong>Lead intake:</strong> Sidebar → Leads → + New Lead (manual or AI parse)</li>
            <li><strong>Comps:</strong> Lead detail → Comparable sales panel</li>
            <li><strong>Calculator:</strong> Lead detail → Comping calculator (autosaves)</li>
            <li><strong>Notes/Calls:</strong> Lead detail → Notes panel (Initial Call, Follow Up, etc.)</li>
            <li><strong>SMS:</strong> Lead detail → SMS panel (Quick Send pills)</li>
            <li><strong>Contracts:</strong> Lead detail → Contracts panel (PSA, Assignment, etc.)</li>
            <li><strong>Glossary:</strong> Sidebar → Knowledge Base</li>
            <li><strong>Awaiting follow-up:</strong> Sidebar → Overview → "Awaiting response" tile</li>
          </Bullets>
        </Card>

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <Btn variant="ghost" onClick={() => navigate('/agent/knowledge')}>📚 Open Knowledge Base →</Btn>
          <Btn variant="gold" onClick={() => navigate('/agent/leads?new=1')}>+ Practice on a real lead</Btn>
        </div>
      </Section>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// LEAD SUBMITTER GUIDE — Zillow lead-finding + submission flow
// ─────────────────────────────────────────────────────────────
function LeadSubmitterGuide() {
  const navigate = useNavigate();
  return (
    <>
      <PageHeader
        eyebrow="Training guide · Lead Agent"
        title={<>Property Research & <em style={{ fontStyle: 'italic', color: BURG }}>Comp Collection</em>.</>}
        sub="Find residential properties that match company criteria, collect details, run comparable sales research, organize the packet, hand it off."
      />

      <Section id="ls-role" num="✦" title="Role overview" subtitle="What you do — and what you do NOT do">
        <p>The Leads Research Agent is responsible for:</p>
        <Bullets>
          <li>Searching for residential property listings</li>
          <li>Identifying properties that match internal criteria</li>
          <li>Collecting property details</li>
          <li>Running comparable sales research (comps)</li>
          <li>Organizing lead information</li>
          <li>Passing completed property packets to the next department</li>
        </Bullets>
        <Tip>
          <strong>This role is strictly administrative and research-based.</strong> Your responsibility is to <em>find and organize property opportunities that meet company search criteria</em>.
        </Tip>
        <H3>You are NOT responsible for:</H3>
        <Bullets>
          <li>Negotiating with property owners</li>
          <li>Discussing investments</li>
          <li>Discussing contracts</li>
          <li>Discussing pricing strategy</li>
          <li>Explaining company operations</li>
          <li>Closing transactions</li>
        </Bullets>
        <p>Your job is <strong>research, organization, and accuracy</strong>.</p>
      </Section>

      <Section id="ls-overview" num="①" title="Property search overview" subtitle="Where the Lead Agent fits in">
        <p>The company uses a multi-step internal review process. Your responsibility covers <strong>only</strong>:</p>
        <Bullets>
          <li>Finding residential properties</li>
          <li>Collecting information</li>
          <li>Running comps</li>
          <li>Organizing property packets</li>
          <li>Passing information to the next department</li>
        </Bullets>
        <Tip><strong>You are NOT involved after the research stage.</strong> Once you submit a property, the next department takes over — owner contact, negotiation, contracts, closing.</Tip>
      </Section>

      <Section id="ls-areas" num="②" title="How to pick search areas" subtitle="Where the deals live">
        <H3>Ideal search areas</H3>
        <Bullets>
          <li>Mid-size cities</li>
          <li>Older residential neighborhoods</li>
          <li>Areas with older homes</li>
          <li>Single-family home neighborhoods</li>
          <li>Areas with active home sales</li>
        </Bullets>

        <H3>Examples</H3>
        <Bullets>
          <li>Jacksonville, Florida</li>
          <li>Fayetteville, North Carolina</li>
          <li>Memphis, Tennessee</li>
          <li>Birmingham, Alabama</li>
          <li>Indianapolis, Indiana</li>
          <li>Atlanta outskirts</li>
        </Bullets>
      </Section>

      <Section id="ls-zillow" num="③" title="Where to find properties" subtitle="Primary search tool: Zillow">
        <H3>Keywords to search</H3>
        <p>Search residential listings for terms that suggest the property may need updates or repairs. These keywords help identify properties that meet internal search guidelines:</p>
        <Bullets>
          <li><code>As-Is</code></li>
          <li><code>Needs TLC</code></li>
          <li><code>Handyman special</code></li>
          <li><code>Needs work</code></li>
          <li><code>Estate sale</code></li>
          <li><code>Vacant</code></li>
          <li><code>Reduced price</code></li>
          <li><code>Distressed</code></li>
          <li><code>Cash only</code></li>
          <li><code>Investor special</code></li>
        </Bullets>
      </Section>

      <Section id="ls-search-steps" num="④" title="How to search Zillow" subtitle="The exact step-by-step Zillow process">
        <ol style={s.list}>
          <li>
            <strong>Step 1 — Go to Zillow.com.</strong>
          </li>
          <li>
            <strong>Step 2 — Type in your assigned city.</strong> Example: <em>Jacksonville, Florida</em>.
          </li>
          <li>
            <strong>Step 3 — Expand the search area.</strong> When Zillow draws the blue border around the city:
            <Bullets>
              <li>Click outside the blue border</li>
              <li>Expand the search radius</li>
              <li>Review surrounding neighborhoods</li>
            </Bullets>
          </li>
          <li>
            <strong>Step 4 — Apply filters.</strong> Recommended filters:
            <Bullets>
              <li>Houses only</li>
              <li>Single family homes</li>
              <li>Older homes</li>
              <li>Price reductions</li>
              <li>2–4 bedrooms</li>
            </Bullets>
          </li>
          <li>
            <strong>Step 5 — Review listing details carefully.</strong> Look for:
            <Bullets>
              <li>Poor condition photos</li>
              <li>Outdated interiors</li>
              <li>Overgrown landscaping</li>
              <li>Damaged roofing</li>
              <li>Vacancy indicators</li>
              <li>Visible repair needs</li>
            </Bullets>
          </li>
        </ol>
      </Section>

      <Section id="ls-collect" num="⑤" title="Property information collection" subtitle="What to capture for every property">
        <H3>Basic property information</H3>
        <Bullets>
          <li>Property address</li>
          <li>Listing link</li>
          <li>Bedrooms</li>
          <li>Bathrooms</li>
          <li>Square footage</li>
          <li>Lot size</li>
          <li>Year built</li>
        </Bullets>

        <H3>Property notes — document</H3>
        <Bullets>
          <li>Visible repairs needed</li>
          <li>Exterior condition</li>
          <li>Interior condition</li>
          <li>Vacancy indicators</li>
          <li>Listing remarks</li>
        </Bullets>
      </Section>

      <Section id="ls-submit" num="⑥" title="Submit the lead in HomeIQa" subtitle="One paste does the whole form">
        <H3>The simple way (recommended)</H3>
        <ol style={s.list}>
          <li>On the Zillow / Redfin / Realtor listing, hit <strong>Cmd+A</strong> (Mac) or <strong>Ctrl+A</strong> (Windows) to select everything on the page → <strong>Cmd+C / Ctrl+C</strong> to copy.</li>
          <li>In HomeIQa, click <strong>Submit a Lead</strong> in the sidebar.</li>
          <li>Click the gold <strong>📋 Paste from clipboard</strong> button at the top — that's it.</li>
          <li>The AI reads the listing and fills <strong>address, city, state, zip, owner name, phone, asking price, beds, baths, sqft, and the listing URL</strong> for you.</li>
          <li>A green <strong>✓ URL matches address</strong> badge confirms the URL and address agree. If you see ⚠ instead, double-check.</li>
          <li>Glance at the auto-filled fields, fix anything that looks off, then click <strong>Submit Lead →</strong>.</li>
        </ol>

        <Tip>
          <strong>If the clipboard button doesn't work</strong> (some browsers block it), just right-click in the textarea below it → Paste. The form auto-parses the moment you paste.
        </Tip>

        <H3>Manual entry (if you prefer)</H3>
        <ol style={s.list}>
          <li>Click <strong>Submit a Lead</strong> in the sidebar.</li>
          <li>Skip the paste box and go straight to the <strong>Review and submit</strong> card below it.</li>
          <li>Required: <strong>Owner phone, Listing URL, Street address</strong>. Owner name defaults to "Property Owner" if you leave it blank.</li>
          <li>Optional: asking price, beds, baths, sqft, email.</li>
          <li>Click <strong>Submit Lead →</strong>.</li>
        </ol>
      </Section>

      <Section id="ls-comps" num="⑦" title="Run comparable sales research" subtitle="Open the submission, pull comps, hand off">
        <p>After you submit a lead, click it from <strong>My Submissions</strong>. You'll see a two-step timeline:</p>

        <H3>Step ① Property Details</H3>
        <p>Verify the address, beds, baths, and sqft are correct. Edit anything that the parser got wrong.</p>

        <H3>Step ② Comparable Sales</H3>
        <Bullets>
          <li>Click <strong>🔄 RentCast</strong>. The system pulls recent comparable sales nearby and stores them on the lead.</li>
          <li>If RentCast returns nothing, click <strong>+ Add manual</strong> and paste comps from Zillow's Sold listings (address, beds/baths/sqft, sale date, sale price). Aim for at least 3 comps within ~1 mile, sold in the last 6 months.</li>
          <li>Comps you add manually count alongside RentCast's. They show up tagged with a gold "Manual" pill so the next department knows where they came from.</li>
        </Bullets>

        <Tip>
          <strong>Don't let ChatGPT do your comps either.</strong> Go find them yourself. Twin properties (same beds, similar sqft, same neighborhood, recently renovated) are what we want.
        </Tip>
      </Section>

      <Section id="ls-handoff" num="⑧" title="What happens after you submit" subtitle="Hand-off to the next department">
        <Bullets>
          <li>The lead lands in the HomeIQa pipeline tagged with <strong>your name + the date/time you submitted</strong>.</li>
          <li>Open <strong>My Submissions</strong> in the sidebar to see all your leads and their current status.</li>
          <li>Status updates in real time as the team works each lead: New → Contacted → Negotiating → Under Contract → Assigned → Closed (or Dead).</li>
          <li>You'll see a green checkmark on Property & Comps once those steps are complete on a lead.</li>
        </Bullets>

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <Btn variant="ghost" onClick={() => navigate('/agent/submissions')}>📋 Open My Submissions</Btn>
          <Btn variant="gold" onClick={() => navigate('/agent/submit')}>+ Submit a Lead now</Btn>
        </div>
      </Section>

      <Section id="ls-cheat" num="★" title="Quick reference" subtitle="Print this. Tape it next to your monitor.">
        <Card title="The whole process on one screen">
          <table style={s.cheat}>
            <tbody>
              <tr><td style={s.cheatStep}>①</td><td style={s.cheatLabel}>Pick a search area</td><td style={s.cheatHow}>Mid-size city · older neighborhoods · single-family · active sales</td></tr>
              <tr><td style={s.cheatStep}>②</td><td style={s.cheatLabel}>Search Zillow</td><td style={s.cheatHow}>Keywords: as-is · TLC · handyman · vacant · estate sale · cash only · investor special</td></tr>
              <tr><td style={s.cheatStep}>③</td><td style={s.cheatLabel}>Expand & filter</td><td style={s.cheatHow}>Click outside the blue border · Houses only · Single family · 2–4 bd · price reductions</td></tr>
              <tr><td style={s.cheatStep}>④</td><td style={s.cheatLabel}>Collect details</td><td style={s.cheatHow}>Address · listing URL · beds · baths · sqft · year built · condition notes</td></tr>
              <tr><td style={s.cheatStep}>⑤</td><td style={s.cheatLabel}>Submit in HomeIQa</td><td style={s.cheatHow}>Cmd+A → Cmd+C on listing → click 📋 Paste from clipboard → Submit</td></tr>
              <tr><td style={s.cheatStep}>⑥</td><td style={s.cheatLabel}>Run comps</td><td style={s.cheatHow}>Open submission · 🔄 RentCast · or + Add manual from Zillow Sold</td></tr>
              <tr><td style={s.cheatStep}>★</td><td style={s.cheatLabel}>Hand off</td><td style={s.cheatHow}>Done. The next department takes it from here.</td></tr>
            </tbody>
          </table>
        </Card>
      </Section>
    </>
  );
}

// ── Layout primitives ──
const Section = ({ id, num, title, subtitle, children }) => (
  <section id={id} style={s.section}>
    <div style={s.sectionHead}>
      <div style={s.sectionNum}>{num}</div>
      <div>
        <div style={s.sectionEyebrow}>Chapter</div>
        <h2 style={s.sectionTitle}>{title}</h2>
        {subtitle && <div style={s.sectionSub}>{subtitle}</div>}
      </div>
    </div>
    <div style={s.sectionBody}>{children}</div>
  </section>
);

const H3 = ({ children }) => <h3 style={s.h3}>{children}</h3>;
const Concept = ({ children }) => <div style={s.concept}>{children}</div>;
const Bullets = ({ children }) => <ul style={s.bullets}>{children}</ul>;

const Tip = ({ children }) => (
  <div style={s.tip}>
    <div style={s.tipLabel}>Tip</div>
    <div>{children}</div>
  </div>
);

const Formula = ({ label, body, note }) => (
  <div style={s.formula}>
    <div style={s.formulaLabel}>{label}</div>
    <div style={s.formulaBody}>{body}</div>
    {note && <div style={s.formulaNote}>{note}</div>}
  </div>
);

const Example = ({ label, children }) => (
  <div style={s.example}>
    <div style={s.exampleLabel}>{label}</div>
    <div>{children}</div>
  </div>
);

const s = {
  sourceLink: { fontSize: 12, color: BURG, textDecoration: 'none', fontFamily: "'DM Sans', sans-serif", padding: '5px 12px', border: `1px solid ${BORDER}`, borderRadius: 999, transition: 'all 0.15s ease' },
  videoNote: { fontSize: 13, color: MID, lineHeight: 1.65, marginBottom: 16 },
  videoFrame: { position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 8, background: '#000', border: `1px solid ${BORDER}`, boxShadow: `0 8px 28px rgba(74,18,32,0.12)`, maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' },
  videoIframe: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' },

  toc: { listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 4 },
  tocItem: { padding: 0 },
  tocLink: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: BURG_DK, textAlign: 'left', width: '100%', borderRadius: 4, transition: 'background 0.15s ease' },
  tocNum: { fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: GOLD, fontWeight: 600, minWidth: 22 },

  section: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '32px 36px', marginBottom: 22, scrollMarginTop: 24 },
  sectionHead: { display: 'flex', alignItems: 'flex-start', gap: 18, marginBottom: 24, paddingBottom: 18, borderBottom: `1px solid ${BORDER}` },
  sectionNum: { fontFamily: "'Cormorant Garamond', serif", fontSize: 56, fontWeight: 600, color: GOLD, lineHeight: 1, letterSpacing: '-2px', minWidth: 60, textAlign: 'center', paddingTop: 4 },
  sectionEyebrow: { fontSize: 10, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD, marginBottom: 6 },
  sectionTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 500, color: BURG_DK, letterSpacing: '-0.4px', lineHeight: 1.15, margin: 0 },
  sectionSub: { fontSize: 13, color: MID, lineHeight: 1.55, marginTop: 6 },
  sectionBody: { fontSize: 14, color: '#1C1C1C', lineHeight: 1.7 },

  h3: { fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 500, color: BURG, marginTop: 26, marginBottom: 10, letterSpacing: '-0.2px' },
  list: { paddingLeft: 22, marginTop: 8, marginBottom: 14, lineHeight: 1.75 },
  bullets: { paddingLeft: 22, marginTop: 8, marginBottom: 14, lineHeight: 1.75, listStyle: 'disc' },

  concept: { background: GOLD_PALE, borderLeft: `3px solid ${GOLD}`, padding: '16px 20px', borderRadius: '0 6px 6px 0', marginBottom: 16, fontSize: 14, color: BURG_DK, lineHeight: 1.65 },
  tip: { background: CREAM, border: `1px solid ${BORDER}`, borderLeft: `3px solid ${BURG}`, padding: '14px 18px', borderRadius: '0 4px 4px 0', marginTop: 14, marginBottom: 14, fontSize: 13, color: BURG_DK, lineHeight: 1.65 },
  tipLabel: { fontSize: 9, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: BURG, marginBottom: 6 },

  formula: { background: '#1C1C1C', color: '#fff', borderRadius: 8, padding: '20px 24px', textAlign: 'center', margin: '20px 0', borderLeft: `4px solid ${GOLD}` },
  formulaLabel: { fontSize: 10, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD, marginBottom: 10 },
  formulaBody: { fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 500, lineHeight: 1.3, letterSpacing: '0.02em' },
  formulaNote: { fontSize: 12, color: 'rgba(255,255,255,0.65)', fontStyle: 'italic', marginTop: 10 },

  example: { background: '#FAFAFA', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '16px 20px', marginTop: 14, marginBottom: 14, fontSize: 13, color: '#1C1C1C', lineHeight: 1.75 },
  exampleLabel: { fontSize: 10, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD, marginBottom: 10 },

  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 12, marginBottom: 14 },
  th: { textAlign: 'left', padding: '10px 12px', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: LIGHT, borderBottom: `1px solid ${BORDER}`, fontWeight: 500 },
  td: { padding: '10px 12px', borderBottom: `1px solid ${BORDER}`, color: '#1C1C1C', verticalAlign: 'top' },
  tdNum: { padding: '10px 12px', borderBottom: `1px solid ${BORDER}`, color: BURG_DK, fontWeight: 600, fontFamily: 'monospace' },

  cheat: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  cheatStep: { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: GOLD, fontWeight: 600, padding: '10px 14px', verticalAlign: 'middle', width: 50, borderBottom: `1px solid ${BORDER}` },
  cheatLabel: { padding: '10px 14px', fontWeight: 500, color: BURG_DK, borderBottom: `1px solid ${BORDER}`, width: 160 },
  cheatHow: { padding: '10px 14px', color: MID, borderBottom: `1px solid ${BORDER}`, fontStyle: 'italic' }
};
